<template>
  <div class="desktop-betting-section">
    <div class="desktop-betting-header">
      <h2 class="desktop-section-title">
        {{ $t('betting.selectYourMarket') }}
      </h2>
    </div>
    
    <!-- Tab Navigation -->
    <div class="desktop-tab-navigation">
      <button
        class="desktop-tab-btn"
        :class="{ active: activeTab === 'main' }"
        @click="activeTab = 'main'"
      >
        {{ $t('betting.mainMarkets') }}
      </button>
      <button
        class="desktop-tab-btn"
        :class="{ active: activeTab === 'bonus' }"
        @click="activeTab = 'bonus'"
      >
        {{ $t('betting.bonusMarkets') }} 🎁
      </button>
    </div>
    
    <!-- Main Tab Content -->
    <div v-if="activeTab === 'main'" class="desktop-tab-content active-tab-content">
      <div class="desktop-market-dropdown-section">
        <div class="desktop-dropdown-container">
          <label class="desktop-dropdown-label">{{ $t('betting.selectMarket') }}:</label>
          <select 
            v-model="selectedMainMarket" 
            @change="onMainMarketChange"
            class="desktop-market-select"
          >
            <option value="">{{ $t('betting.chooseMarket') }}</option>
            <option 
              v-for="market in allMainMarkets" 
              :key="market.id" 
              :value="market.id"
            >
              {{ getMarketDisplayLabel(market) }}
            </option>
          </select>
        </div>
      </div>

      <!-- Number Selection Section -->
      <div
        v-if="selectedMainMarketData && showMainNumberSelection"
        class="desktop-number-selection-section"
      >
        <div class="desktop-number-selection-header">
          <span class="desktop-number-selection-title">{{ $t('betting.selectNumbers') }}</span>
          <span v-if="currentOdds && selectedNumbers.length > 0" class="desktop-number-selection-odds">{{ $t('betting.odds') }}: {{ formatOdds(currentOdds) }}</span>
        </div>

        <!-- Main Number Grid -->
        <div v-if="!isBonusOnlyMarket && !isMainIncludingBonusMarket" class="desktop-number-grid">
          <div
            v-for="n in actualBallsMax"
            :key="n"
            class="desktop-number-circle"
            :class="{
              selected: selectedNumbers.includes(n),
              hot: hotNumbers.includes(n),
              cold: coldNumbers.includes(n),
              disabled: (isSameDrumMarket) && selectedBonusNumbers.includes(n),
              'quick-pick': selectedNumbers.includes(n) && selectedNumbers.length === actualBalls,
            }"
            @click="toggleNumber(n)"
          >
            {{ n }}
          </div>
        </div>

        <!-- Main Including Bonus Number Grid (Single Grid) -->
        <div v-if="isMainIncludingBonusMarket" class="desktop-number-grid">
          <div
            v-for="n in actualBallsMax"
            :key="n"
            class="desktop-number-circle"
            :class="{
              selected: selectedNumbers.includes(n),
              hot: hotNumbers.includes(n),
              cold: coldNumbers.includes(n),
              'quick-pick': selectedNumbers.includes(n) && selectedNumbers.length >= 1,
            }"
            @click="toggleMainIncludingBonusNumber(n)"
          >
            {{ n }}
          </div>
        </div>

        <!-- Bonus ball colour (UK49 color combo) -->
        <div v-if="isColorComboMarket" class="desktop-bonus-section desktop-color-combo-section">
          <BonusBallColorRow v-model="selectedBonusColor" />
        </div>

        <!-- Bonus Ball Selection -->
        <div v-if="(requiresBonusBall || isSameDrumMarket || isBonusOnlyMarket) && selectedMainMarket && !isMainIncludingBonusMarket && !isColorComboMarket" class="desktop-bonus-section">
          <div class="desktop-bonus-title">
            {{ isSameDrumBonusMarket ? '' : (isBonusOnlyMarket ? $t('betting.selectBonusNumber') : $t('betting.bonusNumber')) }}
          </div>
          <div v-if="!isSameDrumBonusMarket || isBonusOnlyMarket" class="desktop-bonus-number-grid">
            <div
              v-for="n in bonusBallsMax"
              :key="`bonus-${n}`"
              class="desktop-number-circle desktop-bonus-circle"
              :class="{
                selected: selectedBonusNumbers.includes(n),
                hot: hotNumbers.includes(n),
                cold: coldNumbers.includes(n),
                disabled: (isSameDrumMarket) && selectedNumbers.includes(n),
              }"
              @click="toggleBonusNumber(n)"
            >
              {{ n }}
            </div>
          </div>
        </div>

        <!-- Selected Market Info -->
        <div class="desktop-market-info">
          <div class="desktop-market-display" :class="{ 'center-layout': !((isBonusOnlyMarket && selectedBonusNumbers.length > 0) || (!isBonusOnlyMarket && selectedNumbers.length > 0)) }">
            <span class="desktop-market-text">
              <template v-if="isMainIncludingBonusMarket">
                <template v-if="selectedNumbers.length === 0">
                  {{ $t('betting.pleaseSelectNumbers') }}
                </template>
                <template v-else>
                  {{ $t('betting.selectedBalls', { count: selectedNumbers.length }) }}
                </template>
              </template>
              <template v-else-if="(isBonusOnlyMarket && selectedBonusNumbers.length > 0) || (!isBonusOnlyMarket && selectedNumbers.length > 0)">
                {{ isBonusOnlyMarket 
                  ? $t('betting.selectedBonusBalls', { count: selectedBonusNumbers.length })
                  : $t('betting.selectedBalls', { count: selectedNumbers.length })
                }}
              </template>
              <template v-else>
                {{ $t('betting.pleaseSelectNumber') }}
              </template>
            </span>
            <span v-if="selectedNumbers.length > 0 || (isColorComboMarket && selectedBonusColor)" class="desktop-market-odds">{{ formatOdds(currentOdds) }}</span>
          </div>
        </div>
      </div>

      <!-- Action Buttons -->
      <div
        v-if="selectedMainMarket && showMainNumberSelection"
        class="desktop-action-buttons"
      >
        <button 
          class="desktop-quick-pick-btn"
          @click="quickPick"
        >
          {{ $t('betting.quickPick') }}
        </button>
        <button 
          class="desktop-add-line-btn"
          :disabled="!canAddLine"
          @click="addLine"
        >
          {{ $t('betting.addLine') }}
        </button>
      </div>
    </div>

    <!-- Bonus Tab Content -->
    <div v-if="activeTab === 'bonus'" class="desktop-tab-content active-tab-content">
      <div class="desktop-bonus-markets">
        <div 
          v-for="group in bonusMarkets" 
          :key="group.id"
          class="desktop-bonus-group"
        >
          <div class="desktop-bonus-group-title">{{ group.name }}</div>
          <div class="desktop-bonus-markets-grid">
            <div 
              v-for="market in group.markets" 
              :key="market.id"
              class="desktop-bonus-market-item"
            >
              <div class="desktop-bonus-market-header">
                <div class="desktop-bonus-market-description">{{ getMarketDisplayLabel(market) }}</div>
              </div>
              <SameColorBallsSelector
                v-if="isSameColorBallsMarket(market.slug)"
                :market="market"
                @select="(outcome) => selectSameColorBalls(market, outcome, group)"
              />
              <div v-else class="desktop-bonus-outcomes">
                <div
                  v-for="outcome in market.outcomes"
                  :key="outcome.id"
                  class="desktop-bonus-outcome"
                  :class="{ 'selected': isMarketSelected(market, outcome) }"
                  @click="selectBonusMarket(market, outcome, group)"
                >
                  <span class="desktop-bonus-outcome-label">{{ formatOutcomeText(outcome) }}</span>
                  <span class="desktop-bonus-outcome-odds">{{ formatOdds(parseFloat(outcome.odds)) }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, nextTick, defineProps, defineEmits } from 'vue';
import { formatOdds } from '../../utils/currency';
import { getMarketDisplayLabel } from '../../utils/market';
import SameColorBallsSelector from '../SameColorBallsSelector.vue';
import BonusBallColorRow from '../BonusBallColorRow.vue';
import {
  isSameColorBallsMarket,
  formatSameColorBetText,
  buildSameColorMarketSelection,
  pickRandomBallColor,
  type BallColorSlug,
} from '@/constants/sameColorBalls';
import {
  isStandardBetPlusColorBonusMarket,
  buildStandardBetPlusColorBonusSelection,
  resolveColorBonusOutcome,
} from '@/constants/standardBetPlusColorBonus';

interface MarketOutcome {
  id: number;
  game_market_id?: number;
  text: string;
  select_numbers: boolean;
  pick: number;
  slug: string;
  odds: string;
  specifier: string | null;
}

interface Market {
  id: number;
  name: string;
  slug: string;
  description: string;
  ordering: number;
  outcomes: MarketOutcome[];
}

interface MarketGroup {
  id: number;
  name: string;
  slug: string;
  ordering: number;
  has_bonus: boolean;
  markets: Market[];
  rules: Array<{
    id: number;
    name: string;
    balls_min: number;
    balls_max: number;
    picks_min: number;
    picks_max: number;
    has_bonus: number | boolean;
    max_resulted: number;
    type: string;
  }>;
}

interface SelectedMarket {
  marketGroup: MarketGroup;
  market: Market;
  label: string;
  odds: number;
  select_numbers: boolean;
  balls: number;
  outcome_id: string;
  specifier?: string;
  game_market_id?: number;
}

interface Props {
  bettingMarkets: MarketGroup[];
  selectedMarket: SelectedMarket | null;
  hotNumbers?: number[];
  coldNumbers?: number[];
  betslipLength: number;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  'select-market': [market: SelectedMarket];
  'remove-market': [market: SelectedMarket];
  'toggle-group': [index: number];
  'tab-changed': [tab: 'main' | 'bonus'];
  'add-line': [line: { numbers: number[]; bonus?: number; numBets: number; picks: number }];
  'remove-line': [index: number];
  'add-to-betslip': [lines: Array<{ numbers: number[]; bonus?: number; numBets: number; picks: number }>];
  'clear-all': [];
}>();

// Tab state
const activeTab = ref<'main' | 'bonus'>('main');

// Main market selection
const selectedMainMarket = ref<number | ''>('');
const selectedBonusMarkets = ref<Array<{ market: Market; outcome: MarketOutcome }>>([]);

// Number selection state
const selectedNumbers = ref<number[]>([]);
const selectedBonusNumbers = ref<number[]>([]);
const selectedBonusColor = ref<BallColorSlug | null>(null);
const lines = ref<Array<{ numbers: number[]; bonus?: number; numBets: number; picks: number }>>([]);

// Quick pick pattern memory
const lastQuickPickPattern = ref<{ mainCount: number; bonusCount: number } | null>(null);

// Computed properties to separate markets
const mainMarkets = computed(() => {
  return props.bettingMarkets.filter(group => 
    group.markets.some(market => 
      market.outcomes.some(outcome => outcome.select_numbers)
    )
  );
});

const bonusMarkets = computed(() => {
  return props.bettingMarkets.map(group => ({
    ...group,
    markets: group.markets.filter(market => 
      !isStandardBetPlusColorBonusMarket(market.slug) &&
      market.outcomes.every(outcome => !outcome.select_numbers)
    )
  })).filter(group => group.markets.length > 0);
});

const allMainMarkets = computed(() => {
  const markets: Market[] = [];
  mainMarkets.value.forEach(group => {
    group.markets.forEach(market => {
      if (market.outcomes.some(outcome => outcome.select_numbers)) {
        markets.push(market);
      } else if (isStandardBetPlusColorBonusMarket(market.slug)) {
        markets.push(market);
      }
    });
  });
  
  return markets.sort((a, b) => {
    const isABonusBallSameDrum = a.slug === 'bonus-ball-bet-same-drum-as-standard-numbers';
    const isBBonusBallSameDrum = b.slug === 'bonus-ball-bet-same-drum-as-standard-numbers';
    
    if (isABonusBallSameDrum && !isBBonusBallSameDrum) return 1;
    if (!isABonusBallSameDrum && isBBonusBallSameDrum) return -1;
    return 0;
  });
});

const selectedMainMarketData = computed(() => {
  return allMainMarkets.value.find(market => market.id === selectedMainMarket.value);
});

const selectedMainMarketGroup = computed(() => {
  return mainMarkets.value.find(group => 
    group.markets.some(market => market.id === selectedMainMarket.value)
  );
});

// Ballset calculations
const actualBallsMax = computed(() => {
  if (selectedMainMarketGroup.value?.rules?.length) {
    const mainRule = selectedMainMarketGroup.value.rules.find(rule => rule.type === 'main' || !rule.has_bonus);
    return mainRule ? mainRule.balls_max : 49;
  }
  return 49; // Default
});

const isColorComboMarket = computed(() =>
  isStandardBetPlusColorBonusMarket(selectedMainMarketData.value?.slug ?? '')
);

const showMainNumberSelection = computed(() => {
  if (!selectedMainMarketData.value) return false;
  if (isColorComboMarket.value) return true;
  return selectedMainMarketData.value.outcomes.some((outcome) => outcome.select_numbers);
});

const actualBalls = computed(() => {
  if (isColorComboMarket.value) {
    const picks = selectedMainMarketData.value?.outcomes
      .map((o) => Number(o.pick) || 0)
      .filter((p) => p > 0) ?? [];
    return picks.length > 0 ? Math.min(...picks) : 1;
  }
  if (selectedMainMarketData.value?.outcomes) {
    const firstOutcome = selectedMainMarketData.value.outcomes.find(outcome => outcome.select_numbers);
    return firstOutcome ? firstOutcome.pick : 5;
  }
  return 5; // Default
});

const bonusBallsMax = computed(() => {
  if (isSameDrumMarket.value) {
    return actualBallsMax.value;
  }
  if (selectedMainMarketGroup.value?.rules?.length) {
    const bonusRule = selectedMainMarketGroup.value.rules.find(rule => rule.has_bonus === 1);
    return bonusRule ? bonusRule.balls_max : 20;
  }
  return 20; // Default
});

const maxSelectableBalls = computed(() => {
  if (selectedMainMarketData.value?.outcomes) {
    const picks = selectedMainMarketData.value.outcomes
      .map((outcome) => Number(outcome.pick) || 0)
      .filter((pick) => pick > 0);
    if (picks.length > 0) {
      return Math.max(...picks);
    }
    const maxPick = Math.max(...selectedMainMarketData.value.outcomes
      .filter(outcome => outcome.select_numbers)
      .map(outcome => outcome.pick)
    );
    return maxPick || actualBallsMax.value;
  }
  return actualBallsMax.value;
});

const comboMaxPick = computed(() => {
  if (!selectedMainMarketData.value) return 5;
  const picks = selectedMainMarketData.value.outcomes
    .map((o) => Number(o.pick) || 0)
    .filter((p) => p > 0);
  return picks.length > 0 ? Math.max(...picks) : 5;
});

const requiresBonusBall = computed(() => {
  return selectedMainMarketGroup.value?.has_bonus || false;
});

const isBonusOnlyMarket = computed(() => {
  const slug = selectedMainMarketGroup.value?.slug;
  return slug === 'bonus-ball-only-same-drum' || 
         slug === 'bonus-ball-only-additional-drum';
});

const isSameDrumMarket = computed(() => {
  const slug = selectedMainMarketGroup.value?.slug;
  return slug === 'standard-bet-and-bonus-ball-same-drum' ||
         slug === 'bonus-ball-only-same-drum' ||
         slug === 'standard-bet-bonus-from-same-drum' ||
         slug === 'regular-numbers-plus-bonus-ball-same-drum';
});

const isSameDrumBonusMarket = computed(() => {
  const slug = selectedMainMarketGroup.value?.slug;
  return slug === 'bonus-ball-only-same-drum' ||
         slug === 'standard-bet-bonus-from-same-drum';
});

const isMainIncludingBonusMarket = computed(() => {
  const slug = selectedMainMarketGroup.value?.slug;
  return slug === 'standard-bet-bonus-from-same-drum';
});

const canAddLine = computed(() => {
  if (isColorComboMarket.value) {
    return selectedNumbers.value.length > 0 && selectedBonusColor.value !== null;
  }

  if (isBonusOnlyMarket.value) {
    return selectedBonusNumbers.value.length > 0;
  }
  
  // For main including bonus markets, need at least 1 number (1-6 balls allowed)
  if (isMainIncludingBonusMarket.value) {
    return selectedNumbers.value.length >= 1;
  }
  
  // Check for duplicate numbers in same drum markets
  if (isSameDrumMarket.value && !isMainIncludingBonusMarket.value) {
    const hasDuplicates = selectedNumbers.value.some(mainNum => 
      selectedBonusNumbers.value.includes(mainNum)
    );
    if (hasDuplicates) {
      return false;
    }
    
    return selectedNumbers.value.length > 0 && selectedBonusNumbers.value.length > 0;
  }
  
  if (requiresBonusBall.value && !isSameDrumMarket.value) {
    return selectedNumbers.value.length > 0 && selectedBonusNumbers.value.length > 0;
  }
  return selectedNumbers.value.length > 0;
});

// Computed properties for current odds display
const currentOutcome = computed(() => {
  if (!selectedMainMarketData.value || selectedNumbers.value.length === 0) return null;

  if (isColorComboMarket.value && selectedBonusColor.value) {
    return resolveColorBonusOutcome(
      selectedMainMarketData.value.outcomes,
      selectedBonusColor.value,
      selectedNumbers.value.length
    ) ?? null;
  }

  // For main including bonus markets, use the actual number count
  if (isMainIncludingBonusMarket.value) {
    return selectedMainMarketData.value.outcomes.find(outcome => 
      outcome.select_numbers && outcome.pick === selectedNumbers.value.length
    );
  }
  
  return selectedMainMarketData.value.outcomes.find(outcome => 
    outcome.select_numbers && outcome.pick === selectedNumbers.value.length
  );
});

const currentOdds = computed(() => {
  return currentOutcome.value ? Number(currentOutcome.value.odds) : null;
});

// Use props.hotNumbers and props.coldNumbers if provided
const hotNumbers = computed(() => {
  if (props.hotNumbers && props.hotNumbers.length > 0) {
    return props.hotNumbers;
  }
  return [];
});

const coldNumbers = computed(() => {
  if (props.coldNumbers && props.coldNumbers.length > 0) {
    return props.coldNumbers;
  }
  return [];
});

// Set default market selection
const formatOutcomeText = (outcome: MarketOutcome): string => {
  let text = outcome.text || outcome.id.toString();
  
  // Replace {value} placeholder with specifier value
  if (text.includes('{value}') && outcome.specifier) {
    text = text.replace('{value}', outcome.specifier);
  }
  
  return text;
};

const onMainMarketChange = () => {
  // Clear all number selections when market changes
  selectedNumbers.value = [];
  selectedBonusNumbers.value = [];
  selectedBonusColor.value = null;
  lines.value = [];
  
  if (selectedMainMarket.value && selectedMainMarketData.value && selectedMainMarketGroup.value) {
    if (isColorComboMarket.value) {
      return;
    }
    // Find the first outcome with select_numbers for this market
    const firstOutcome = selectedMainMarketData.value.outcomes.find(outcome => outcome.select_numbers);
    if (firstOutcome) {
      const marketData: SelectedMarket = {
        marketGroup: selectedMainMarketGroup.value,
        market: selectedMainMarketData.value,
        label: formatOutcomeText(firstOutcome),
        odds: Number(firstOutcome.odds),
        select_numbers: firstOutcome.select_numbers,
        balls: firstOutcome.pick,
        outcome_id: firstOutcome.id.toString()
      };
      emit('select-market', marketData);
    }
  }
};

const setDefaultMainMarket = () => {
  if (allMainMarkets.value.length > 0 && !selectedMainMarket.value) {
    selectedMainMarket.value = allMainMarkets.value[0].id;
    onMainMarketChange();
  }
};

// Watch for changes in allMainMarkets to set default when data is available
watch(allMainMarkets, (newMarkets) => {
  if (newMarkets.length > 0 && !selectedMainMarket.value) {
    setDefaultMainMarket();
  }
}, { immediate: true });

// Watch for tab changes to clear main market selection when switching to bonus
watch(activeTab, (newTab) => {
  emit('tab-changed', newTab);
  if (newTab === 'bonus') {
    selectedMainMarket.value = '';
  } else if (newTab === 'main' && !selectedMainMarket.value) {
    setDefaultMainMarket();
  }
});

// Initialize default selection after mount to avoid parent update during setup
nextTick(() => {
  setDefaultMainMarket();
});

const selectSameColorBalls = (market: Market, outcome: MarketOutcome, group: MarketGroup) => {
  const label = formatSameColorBetText(outcome);
  const selection = buildSameColorMarketSelection(market, outcome);

  emit('select-market', {
    marketGroup: group,
    market: market,
    label: label,
    select_numbers: outcome.select_numbers,
    balls: outcome.pick,
    ...selection,
  });
};

const updateSelectedMarketForColorCombo = () => {
  if (!selectedMainMarketData.value || !selectedMainMarketGroup.value) return;
  if (!selectedBonusColor.value || selectedNumbers.value.length === 0) return;

  const outcome = resolveColorBonusOutcome(
    selectedMainMarketData.value.outcomes,
    selectedBonusColor.value,
    selectedNumbers.value.length
  );
  if (!outcome) return;

  const selection = buildStandardBetPlusColorBonusSelection(
    selectedNumbers.value,
    outcome
  );

  emit('select-market', {
    marketGroup: selectedMainMarketGroup.value,
    market: selectedMainMarketData.value,
    label: selection.label,
    odds: selection.odds,
    select_numbers: true,
    balls: selection.picks,
    outcome_id: selection.outcome_id,
    game_market_id: selection.game_market_id,
  });
};

const selectBonusMarket = (market: Market, outcome: MarketOutcome, group: MarketGroup) => {
  // Check if this market/outcome is already selected
  const isAlreadySelected = selectedBonusMarkets.value.some(selected => 
    selected.market.id === market.id &&
    selected.outcome.id === outcome.id
  );
  
  if (isAlreadySelected) {
    // Remove from selection (toggle off)
    selectedBonusMarkets.value = selectedBonusMarkets.value.filter(selected => 
      !(selected.market.id === market.id &&
        selected.outcome.id === outcome.id)
    );
    
    // Emit remove event to parent
    emit('remove-market', {
      marketGroup: group,
      market: market,
      label: formatOutcomeText(outcome),
      odds: Number(outcome.odds),
      select_numbers: outcome.select_numbers,
      balls: outcome.pick,
      outcome_id: outcome.id.toString()
    });
  } else {
    // Add to selection (toggle on)
    selectedBonusMarkets.value.push({ market, outcome });
    
    // Emit add event to parent
    emit('select-market', {
      marketGroup: group,
      market: market,
      label: formatOutcomeText(outcome),
      odds: Number(outcome.odds),
      select_numbers: outcome.select_numbers,
      balls: outcome.pick,
      outcome_id: outcome.id.toString()
    });
  }
};

const isMarketSelected = (market: Market, outcome: MarketOutcome): boolean => {
  // Check if this is a bonus market selection
  if (selectedBonusMarkets.value.length > 0) {
    return selectedBonusMarkets.value.some(selected => 
      selected.market.id === market.id &&
      selected.outcome.id === outcome.id
    );
  }
  
  // Check if this is the currently selected main market
  if (selectedMainMarket.value === market.id) {
    return true;
  }
  
  return false;
};

const formatMarketDescription = (market: Market): string => {
  let description = market.description || '';
  
  // Find the first outcome with a specifier to replace {value} in description
  const outcomeWithSpecifier = market.outcomes.find(outcome => outcome.specifier);
  if (outcomeWithSpecifier?.specifier && description && description.includes('{value}')) {
    description = description.replace('{value}', outcomeWithSpecifier.specifier);
  }
  
  return description;
};

const toggleMainIncludingBonusNumber = (number: number) => {
  const index = selectedNumbers.value.indexOf(number);
  
  if (index === -1) {
    // Add number if we have space (max 6 main numbers)
    if (selectedNumbers.value.length < 6) {
      selectedNumbers.value.push(number);
    }
  } else {
    // Remove number and shift remaining numbers
    selectedNumbers.value.splice(index, 1);
  }
  
  // Update market selection with current number count
  if (isColorComboMarket.value) {
    updateSelectedMarketForColorCombo();
  } else {
    updateSelectedMarketForBalls(selectedNumbers.value.length);
  }
};

const toggleNumber = (number: number) => {
  if (isSameDrumMarket.value && selectedBonusNumbers.value.includes(number)) {
    return;
  }
  
  if (selectedNumbers.value.includes(number)) {
    selectedNumbers.value = selectedNumbers.value.filter(n => n !== number);
  } else {
    if (selectedNumbers.value.length < maxSelectableBalls.value) {
      selectedNumbers.value.push(number);
    }
  }
  
  // Update market selection with current number count
  if (isColorComboMarket.value) {
    updateSelectedMarketForColorCombo();
  } else {
    updateSelectedMarketForBalls(selectedNumbers.value.length);
  }
};

const toggleBonusNumber = (number: number) => {
  if (isSameDrumMarket.value && selectedNumbers.value.includes(number)) {
    return;
  }
  
  if (selectedBonusNumbers.value.includes(number)) {
    selectedBonusNumbers.value = selectedBonusNumbers.value.filter(n => n !== number);
  } else {
    // Clear existing selection and select new one
    selectedBonusNumbers.value = [number];
  }
};

const updateSelectedMarketForBalls = (numberOfBalls: number) => {
  if (!selectedMainMarketData.value || isColorComboMarket.value) return;
  
  // Find the outcome that matches the number of balls selected
  const matchingOutcome = selectedMainMarketData.value.outcomes.find(outcome => 
    outcome.select_numbers && outcome.pick === numberOfBalls
  );
  
  if (matchingOutcome && selectedMainMarketGroup.value) {
    // Update the selected market with the new outcome
    const marketData: SelectedMarket = {
      marketGroup: selectedMainMarketGroup.value,
      market: selectedMainMarketData.value,
      label: formatOutcomeText(matchingOutcome),
      odds: Number(matchingOutcome.odds),
      select_numbers: matchingOutcome.select_numbers,
      balls: matchingOutcome.pick,
      outcome_id: matchingOutcome.id.toString()
    };
    emit('select-market', marketData);
  }
};

const quickPick = () => {
  if (isBonusOnlyMarket.value) {
    // For bonus-only markets, maintain the same number of bonus selections
    const currentBonusCount = selectedBonusNumbers.value.length;
    const targetBonusCount = currentBonusCount > 0 ? currentBonusCount : 1;
    
    const bonusNumbers = Array.from({ length: bonusBallsMax.value }, (_, i) => i + 1);
    const shuffledBonus = bonusNumbers.sort(() => Math.random() - 0.5);
    selectedBonusNumbers.value = shuffledBonus.slice(0, targetBonusCount);
    
    // Store pattern for bonus-only market
    lastQuickPickPattern.value = { mainCount: 0, bonusCount: targetBonusCount };
  } else {
    // For regular markets, 
    let mainCount: number;
    let bonusCount: number;
    
    // If user has existing selections, maintain the same count but clear previous selections
    if (selectedNumbers.value.length > 0 || selectedBonusNumbers.value.length > 0) {
      mainCount = selectedNumbers.value.length > 0 ? selectedNumbers.value.length : actualBalls.value;
      bonusCount = selectedBonusNumbers.value.length > 0 ? selectedBonusNumbers.value.length : 
                   ((requiresBonusBall.value || isSameDrumMarket.value) ? 1 : 0);
      
      // Clear previous selections before picking new ones
      selectedNumbers.value = [];
      selectedBonusNumbers.value = [];
    } else if (lastQuickPickPattern.value) {
      // Use the same pattern as the last quick pick
      mainCount = lastQuickPickPattern.value.mainCount;
      bonusCount = lastQuickPickPattern.value.bonusCount;
    } else {
      // First time - use current market requirements
      mainCount = actualBalls.value;
      bonusCount = (requiresBonusBall.value || isSameDrumMarket.value) ? 1 : 0;
    }
    
    // Quick pick main numbers
    if (mainCount > 0) {
      const availableNumbers = Array.from({ length: actualBallsMax.value }, (_, i) => i + 1);
      const shuffled = availableNumbers.sort(() => Math.random() - 0.5);
      selectedNumbers.value = shuffled.slice(0, mainCount);
    }
    
    // Quick pick bonus numbers (if required)
    if (bonusCount > 0) {
      const bonusNumbers = Array.from({ length: bonusBallsMax.value }, (_, i) => i + 1);
      const availableBonusNumbers = isSameDrumMarket.value 
        ? bonusNumbers.filter(num => !selectedNumbers.value.includes(num))
        : bonusNumbers;
      
      if (availableBonusNumbers.length > 0) {
        const shuffledBonus = availableBonusNumbers.sort(() => Math.random() - 0.5);
        selectedBonusNumbers.value = shuffledBonus.slice(0, bonusCount);
      } else {
        selectedBonusNumbers.value = [];
      }
    }
    
    // Store the pattern for next quick pick
    lastQuickPickPattern.value = { mainCount, bonusCount };

    if (isColorComboMarket.value) {
      selectedBonusColor.value = pickRandomBallColor();
    }
    
    // Update market selection to match the number of balls selected
    if (isColorComboMarket.value) {
      updateSelectedMarketForColorCombo();
    } else {
      updateSelectedMarketForBalls(selectedNumbers.value.length);
    }
  }
};

const addLine = () => {
  if (!canAddLine.value) return;
  
  const line = {
    numbers: [...selectedNumbers.value],
    bonus: (requiresBonusBall.value || (isSameDrumMarket.value && !isMainIncludingBonusMarket.value) || isBonusOnlyMarket.value) ? selectedBonusNumbers.value[0] : undefined,
    numBets: 1,
    picks: selectedNumbers.value.length
  };
  
  lines.value.push(line);
  
  // Store the pattern for future quick picks
  lastQuickPickPattern.value = {
    mainCount: selectedNumbers.value.length,
    bonusCount: selectedBonusNumbers.value.length
  };
  
  // Emit the line to parent
  emit('add-line', line);
  
  // Clear selections for next line
  selectedNumbers.value = [];
  selectedBonusNumbers.value = [];
  selectedBonusColor.value = null;
};

watch(selectedBonusColor, () => {
  if (isColorComboMarket.value) {
    updateSelectedMarketForColorCombo();
  }
});

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const removeLine = (index: number) => {
  lines.value.splice(index, 1);
  emit('remove-line', index);
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const clearAll = () => {
  lines.value = [];
  selectedNumbers.value = [];
  selectedBonusNumbers.value = [];
  emit('clear-all');
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const addToBetslip = () => {
  emit('add-to-betslip', lines.value);
};

// Reset quick pick pattern when market changes
watch(selectedMainMarket, () => {
  lastQuickPickPattern.value = null;
});

watch(() => props.betslipLength, (newLength, oldLength) => {
  if (oldLength && oldLength > 0 && newLength === 0) {
    selectedBonusMarkets.value = [];
  }
});

// Reset internal state when market is cleared
watch(() => props.selectedMarket, (newMarket) => {
  if (!newMarket) {
    // Reset all internal state when market is cleared
    selectedNumbers.value = [];
    selectedBonusNumbers.value = [];
    lines.value = [];
    lastQuickPickPattern.value = null;
  }
});
</script>

<script lang="ts">
export default {
  name: "DesktopBettingSection",
};
</script>

<style scoped>
.desktop-betting-section {
  background: var(--bg-secondary);
  border-radius: 12px;
  margin-bottom: 16px;
  overflow: hidden;
}

.desktop-betting-header {
  padding: 20px 24px;
  background: var(--bg-tertiary);
  border-bottom: 1px solid var(--border-desktop,--border-color);
}

.desktop-section-title {
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
}

.desktop-tab-navigation {
  display: flex;
  background: var(--bg-tertiary);
  border-bottom: 1px solid var(--border-color);
}

.desktop-tab-btn {
  flex: 1;
  padding: 16px 20px;
  background: none;
  border: none;
  color: var(--text-muted);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  border-bottom: 2px solid transparent;
}

.desktop-tab-btn:hover {
  color: var(--text-primary);
  background: var(--dropdown-item-bg);
}

.desktop-tab-btn.active {
    color: var(--text-primary);
    background: var(--bg-tab-active, var(--bg-primary));
    border-bottom-color: var(--accent-color);
    box-shadow: 0 4px 16px color-mix(in srgb, var(--accent-color) 18%, transparent);
    -webkit-transform: translateY(-2px);
    transform: translateY(-2px);
    font-weight: 700;
}

.desktop-tab-content {
  padding: 24px;
}

.active-tab-content {
  border-radius: 12px;
  background: var(--bg-card);
  box-shadow: 0 4px 12px color-mix(in srgb, var(--accent-color) 12%, transparent);
}

.desktop-market-dropdown-section {
  margin-bottom: 24px;
}

.desktop-dropdown-container {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.desktop-dropdown-label {
  font-size: 14px;
  font-weight: 500;
  color: var(--text-primary);
}

.desktop-market-select {
  padding: 12px 16px;
  background: var(--bg-filter-select, var(--bg-secondary));
  border: 1px solid var(--border-color);
  border-radius: 8px;
  color: var(--text-primary);
  font-size: 14px;
  cursor: pointer;
  transition: border-color 0.2s ease;
}

.desktop-market-select:focus {
  outline: none;
  border-color: var(--accent-color);
}


.desktop-odds {
  color: var(--accent-color);
  margin-left: 8px;
}

.desktop-number-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, 40px);
  gap: 6px;
  margin-bottom: 20px;
  justify-content: start;
}

/* iPad Mini (7.9") and smaller tablets */
@media (max-width: 1024px) and (min-width: 768px) {
  .desktop-number-grid {
    grid-template-columns: repeat(auto-fill, 38px);
    gap: 5px;
  }
}

/* iPad (10.2") and medium tablets */
@media (min-width: 1025px) and (max-width: 1200px) {
  .desktop-number-grid {
    grid-template-columns: repeat(auto-fill, 42px);
    gap: 7px;
  }
}

/* Large tablets and small desktops */
@media (min-width: 1201px) {
  .desktop-number-grid {
    grid-template-columns: repeat(auto-fill, 44px);
    gap: 8px;
  }
}

.desktop-number-circle {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 2px solid var(--border-color);
  background: var(--bg-primary);
  color: var(--text-primary);
}

/* iPad Mini (7.9") and smaller tablets */
@media (max-width: 1024px) and (min-width: 768px) {
  .desktop-number-circle {
    width: 38px;
    height: 38px;
    font-size: 12px;
  }
}

/* iPad (10.2") and medium tablets */
@media (min-width: 1025px) and (max-width: 1200px) {
  .desktop-number-circle {
    width: 42px;
    height: 42px;
    font-size: 13px;
  }
}

/* Large tablets and small desktops */
@media (min-width: 1201px) {
  .desktop-number-circle {
    width: 44px;
    height: 44px;
    font-size: 14px;
  }
}

.desktop-number-circle:hover {
  border-color: var(--accent-color);
  background: var(--bg-hover);
}

.desktop-number-circle.selected {
  background: var(--accent-color);
  border-color: var(--accent-color);
  color: var(--text-ball-selected, white);
}

.desktop-number-circle.disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.desktop-number-circle.quick-pick {
  animation: pulse 1s infinite;
}

.desktop-number-circle.main-number {
  background: var(--accent-color);
  color: white;
}

.desktop-number-circle.bonus-number {
  background: var(--warning-color, var(--bg-warning));
  color: white;
}

.number-type-indicator {
  position: absolute;
  top: 2px;
  right: 2px;
  font-size: 10px;
  font-weight: bold;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  border-radius: 50%;
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
}

@keyframes pulse {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.05); }
}

.desktop-bonus-section {
  margin-top: 24px;
}

.desktop-bonus-title {
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 16px;
  text-align: center;
}

.desktop-bonus-number-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, 40px);
  gap: 6px;
  justify-content: start;
}

/* iPad Mini (7.9") and smaller tablets */
@media (max-width: 1024px) and (min-width: 768px) {
  .desktop-bonus-number-grid {
    grid-template-columns: repeat(auto-fill, 38px);
    gap: 5px;
  }
}

/* iPad (10.2") and medium tablets */
@media (min-width: 1025px) and (max-width: 1200px) {
  .desktop-bonus-number-grid {
    grid-template-columns: repeat(auto-fill, 42px);
    gap: 7px;
  }
}

/* Large tablets and small desktops */
@media (min-width: 1201px) {
  .desktop-bonus-number-grid {
    grid-template-columns: repeat(auto-fill, 44px);
    gap: 8px;
  }
}

.desktop-bonus-circle {
  background: var(--bg-primary);
  border-color: var(--border-color);
}

.desktop-bonus-circle:hover {
  border-color: var(--accent-color);
  background: var(--bg-hover);
}

.desktop-bonus-circle.selected {
  background: var(--accent-color);
  border-color: var(--accent-color);
  color: var(--textBallSelected, white);
}

/* Desktop Number Selection Section */
.desktop-number-selection-section {
  background: var(--bg-view-header, transparent);
  margin-top: 20px;
  padding: 20px;
  border-radius: 12px;
  border: 1px solid var(--border-color);
}

.desktop-number-selection-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid var(--border-color);
}

.desktop-number-selection-title {
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary);
}

.desktop-number-selection-odds {
  font-size: 14px;
  font-weight: 600;
  color: var(--accent-color);
}

.desktop-market-info {
  margin-top: 16px;
  padding: 12px 16px;
  background: var(--bg-tertiary);
  border-radius: 8px;
  border: 1px solid var(--border-color);
}

.desktop-market-display {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.desktop-market-display.center-layout {
  justify-content: center;
}

.desktop-market-text {
  font-size: 14px;
  font-weight: 400;
  color: var(--text-primary);
}

.desktop-market-odds {
  font-size: 16px;
  font-weight: 700;
  color: var(--accent-color);
}

.desktop-action-buttons {
  display: flex;
  gap: 12px;
  margin-top: 24px;
}

.desktop-quick-pick-btn,
.desktop-add-line-btn {
  flex: 1;
  padding: 12px 20px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
}

.desktop-quick-pick-btn {
  background: var(--bg-tertiary);
  color: var(--text-primary);
  border: 1px solid var(--border-btn, var(--border-color));
}

.desktop-quick-pick-btn:hover:not(:disabled) {
  background: var(--bg-hover);
}

.desktop-quick-pick-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.desktop-add-line-btn {
  background: var(--accent-color);
  color: var(--text-button, white);
}

.desktop-add-line-btn:hover:not(:disabled) {
  background: var(--bg-bet-button, var(--bg-button));
}

.desktop-add-line-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.desktop-bonus-markets {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.desktop-bonus-group {
  background: var(--bg-bonus-group, var(--bg-primary));
  border-radius: 8px;
  padding: 16px;
  border: 1px solid var(--border-color);
}

.desktop-bonus-group-title {
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 16px;
}

.desktop-bonus-markets-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 16px;
}

.desktop-bonus-market-item {
  background: var(--bg-view-header, var(--bg-secondary));
  border-radius: 8px;
  padding: 16px;
  border: 1px solid var(--border-color);
}

.desktop-bonus-market-header {
  margin-bottom: 12px;
}

.desktop-bonus-market-description {
  font-size: 14px;
  color: var(--text-primary);
  line-height: 1.4;
}

.desktop-bonus-outcomes {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px;
}

.desktop-bonus-outcome {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px;
  background: var(--bg-primary);
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px solid var(--border-color);
}

.desktop-bonus-outcome:hover {
  border-color: var(--accent-color);
  background: var(--bg-hover);
}

.desktop-bonus-outcome.selected {
  border-color: var(--accent-color);
  background: var(--bg-hover);
  box-shadow: 0 0 0 2px var(--bg-hover);
}

.desktop-bonus-outcome-label {
  font-size: 12px;
  color: var(--text-primary);
}

.desktop-bonus-outcome-odds {
  font-size: 12px;
  font-weight: 600;
  color: var(--text-primary);
}
</style>
